import React, { Component } from "react";
import {
  Box,
  CircularProgress,
  Grid,
  Icon,
  Typography
} from "@material-ui/core";
import PropTypes from "prop-types";
import styled from "styled-components";
import { withRouter } from "react-router-dom";
import { withAlert } from "react-alert";
import { connect } from "react-redux";
import { withTranslation } from "react-i18next";
import ClassService from "../../services/ClassService";
import BackButton from "../../components/BackButton";
import Group from "../../waybee-ui/Group";
import Heading from "../../waybee-ui/Heading";
import SearchableSelect from "../../waybee-ui/SearchableSelect";
import TableGrid from "../../waybee-ui/TableGrid";
import fetchGradeJobQueue from "../../utils/fetchGradeJobQueue";
import {
  isReportGenerating,
  pruneGeneratingByReportId
} from "../../utils/reportGeneratingState";

const LoadContainer = styled.div`
  width: 100%;
  text-align: center;
  padding: 32px 8px;
`;

const ReportIcon = styled(Icon)`
  && {
    color: ${props => props.$color || "#0d9488"};
    font-size: 22px;
    margin-right: 10px;
  }
`;

class Reports extends Component {
  constructor(props) {
    super(props);
    this.state = {
      courses: [],
      filter: {},
      loadingFilters: false,
      loadingReports: false,
      generatingByReportId: {},
      reports: []
    };
  }

  componentDidMount() {
    this.getCourses();
  }

  componentDidUpdate(prevProps) {
    const { jobsState } = this.props;
    const { generatingByReportId } = this.state;
    const jobs = (jobsState && jobsState.jobs) || [];
    const previousJobs =
      (prevProps.jobsState && prevProps.jobsState.jobs) || [];
    const next = pruneGeneratingByReportId(
      generatingByReportId,
      jobs,
      previousJobs
    );
    if (next !== generatingByReportId) {
      this.setState({ generatingByReportId: next });
    }
  }

  getCourses = async () => {
    const { match, alert, t } = this.props;

    this.setState({ loadingFilters: true });

    const classId = match.params.id;
    try {
      const disciplines = await ClassService.getDisciplines(classId);
      this.setState({ courses: disciplines });
      if (disciplines && disciplines.length) {
        this.setFilter("course", disciplines[0]);
        await this.getReports(disciplines[0].idTurmaDisc);
      }
    } catch (e) {
      console.error(e);
      alert.show(t("common.messages.errorFetchDisciplines"), {
        title: t("common.actions.error")
      });
    } finally {
      this.setState({ loadingFilters: false });
    }
  };

  getReports = async idTurmaDisc => {
    try {
      const { match } = this.props;
      const codTurma = match.params.id;

      this.setState({ loadingReports: true });
      const reports = await ClassService.getReports(idTurmaDisc, codTurma);
      this.setState({ reports });
    } catch (e) {
      const { alert, t } = this.props;
      alert.show(t("reports.errorList"), { title: t("common.actions.error") });
      throw e;
    } finally {
      this.setState({ loadingReports: false });
    }
  };

  onSelectItem = async report => {
    const { match, alert, jobsState, t } = this.props;
    const { generatingByReportId } = this.state;
    const jobs = (jobsState && jobsState.jobs) || [];
    if (isReportGenerating(report.id, generatingByReportId, jobs)) return;

    this.setState(prev => ({
      generatingByReportId: {
        ...prev.generatingByReportId,
        [report.id]: "submitting"
      }
    }));

    try {
      const created = await ClassService.createReportJob({
        ...report,
        classId: match.params.id
      });
      const jobId = created && created.id;
      this.setState(prev => ({
        generatingByReportId: {
          ...prev.generatingByReportId,
          [report.id]: jobId || "submitting"
        }
      }));
      fetchGradeJobQueue();
    } catch (e) {
      console.error(e);
      alert.show(t("reports.startError"), {
        title: t("common.actions.error")
      });
      this.setState(prev => {
        const next = { ...prev.generatingByReportId };
        delete next[report.id];
        return { generatingByReportId: next };
      });
    }
  };

  setFilter(field, value) {
    const { filter } = this.state;
    this.setState({
      filter: {
        ...filter,
        [field]: value
      }
    });
  }

  changeCourse = course => {
    this.setFilter("course", course);
    if (course && course.idTurmaDisc != null) {
      this.getReports(course.idTurmaDisc);
    }
  };

  render() {
    const { match, jobsState, t } = this.props;
    const classId = match.params.id;
    const {
      loadingFilters,
      loadingReports,
      courses,
      filter,
      reports,
      generatingByReportId
    } = this.state;
    const { className, size } = this.props;
    const jobs = (jobsState && jobsState.jobs) || [];

    return (
      <Grid container spacing={2}>
        <BackButton to={`/classes/${classId}`} />
        {loadingFilters ? (
          <LoadContainer>
            <CircularProgress />
          </LoadContainer>
        ) : (
          <Grid item xs={12}>
            <Group>
              <Box pb={4} pt={2}>
                <Heading level={1} icon="insert_chart" gutterBottom>
                  {t("reports.title")}
                </Heading>
                <Grid item xs={6}>
                  <SearchableSelect
                    label={t("common.labels.discipline")}
                    options={courses}
                    value={filter.course || null}
                    onChange={this.changeCourse}
                    disableClearable
                    getOptionLabel={o => o.name || ""}
                    getOptionSelected={(a, b) =>
                      String(a.idTurmaDisc) === String(b.idTurmaDisc)}
                    noOptionsText={t("common.messages.noDiscipline")}
                  />
                </Grid>
              </Box>
              {loadingReports ? (
                <LoadContainer>
                  <CircularProgress />
                </LoadContainer>
              ) : (
                <TableGrid className={className}>
                  {reports.map(report => {
                    const isGenerating = isReportGenerating(
                      report.id,
                      generatingByReportId,
                      jobs
                    );
                    return (
                      <TableGrid.Row
                        key={report.id}
                        reportButton={
                          isGenerating ? (
                            <CircularProgress size={22} color="inherit" />
                          ) : (
                            true
                          )
                        }
                        button={!isGenerating}
                        onClick={
                          isGenerating
                            ? undefined
                            : () => this.onSelectItem(report)
                        }
                      >
                        <TableGrid.Title
                          {...(size === "compact"
                            ? {
                                xs: 12
                              }
                            : {
                                xs: 12,
                                sm: 4
                              })}
                        >
                          <Box
                            {...(size === "compact" ? { ml: 1, mt: 1 } : {})}
                            display="flex"
                            flexDirection="column"
                            justifyContent="center"
                            style={{ opacity: isGenerating ? 0.7 : 1 }}
                          >
                            <Box display="flex" alignItems="center">
                              <ReportIcon
                                $color={report.iconColor || "#0d9488"}
                              >
                                {report.icon || "insert_chart"}
                              </ReportIcon>
                              {report.name}
                            </Box>
                            {isGenerating && (
                              <Typography
                                variant="caption"
                                color="textSecondary"
                              >
                                {t("reports.generating")}
                              </Typography>
                            )}
                          </Box>
                        </TableGrid.Title>
                        <TableGrid.Text
                          {...(size === "compact"
                            ? {
                                xs: 12
                              }
                            : {
                                xs: 12,
                                sm: 4
                              })}
                        />
                      </TableGrid.Row>
                    );
                  })}
                </TableGrid>
              )}
            </Group>
          </Grid>
        )}
      </Grid>
    );
  }
}

Reports.defaultProps = {
  className: "",
  size: "normal",
  jobsState: { jobs: [] }
};

Reports.propTypes = {
  alert: PropTypes.shape({
    show: PropTypes.func
  }).isRequired,
  match: PropTypes.shape({
    params: PropTypes.shape({
      id: PropTypes.string
    })
  }).isRequired,
  className: PropTypes.string,
  size: PropTypes.string,
  jobsState: PropTypes.shape({
    jobs: PropTypes.arrayOf(PropTypes.shape())
  }),
  t: PropTypes.func.isRequired
};

const mapStateToProps = state => ({
  jobsState: state.jobsState
});

export default connect(mapStateToProps)(
  withRouter(withAlert()(withTranslation()(Reports)))
);
